import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle, XCircle, ExternalLink, History } from "lucide-react";

const STATUS_STYLE = {
  couvert: { cls: "tag-green", Icon: CheckCircle2, key: "vault.status.covered" },
  partiel: { cls: "tag-orange", Icon: AlertTriangle, key: "vault.status.partial" },
  non_couvert: { cls: "tag-red", Icon: XCircle, key: "vault.status.gap" },
};

export function VaultVerifyButton({ scanId, onVerified }) {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/scan/full/${scanId}/verify`);
      toast.success(`${t("vault.done")} ${data.version_label} · ${data.score_before} → ${data.score_after}/100`);
      onVerified(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("vault.error"));
    } finally { setLoading(false); }
  };
  return (
    <Button onClick={run} disabled={loading} variant="outline"
      className="rounded-lg h-11 border-primary/40 text-primary hover:bg-primary/5" data-testid="vault-verify-btn">
      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
      {loading ? t("vault.running") : t("vault.cta")}
    </Button>
  );
}

export function VaultVerificationPanel({ v, history }) {
  const { t } = useLang();
  if (!v) return null;
  const delta = v.score_delta ?? (v.score_after - v.score_before);
  return (
    <div className="hairline p-6 space-y-6" data-testid="vault-verification-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[hsl(var(--status-green))]" /> {t("vault.panel.title")}</p>
          <h2 className="text-lg md:text-lg font-serif">
            {v.version_label} · {new Date(v.verified_at).toLocaleString()}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("vault.score")}</p>
            <p className="font-serif text-2xl" data-testid="vault-score-after">
              <span className="text-muted-foreground line-through text-base mr-2">{v.score_before}</span>
              {v.score_after}/100 · {v.grade_after}
            </p>
          </div>
          <span className={`px-2 py-1 rounded-lg font-mono text-xs ${delta > 0 ? "tag-green" : delta < 0 ? "tag-red" : "tag-orange"}`} data-testid="vault-delta">
            {delta > 0 ? "+" : ""}{delta} pts
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Metric label={t("vault.status.covered")} value={v.covered_count} tone="green" testid="vault-covered" />
        <Metric label={t("vault.status.partial")} value={v.partial_count} tone="orange" testid="vault-partial" />
        <Metric label={t("vault.status.gap")} value={v.gap_count} tone="red" testid="vault-gaps" />
      </div>

      {(v.vault_labels || []).length > 0 && (
        <div>
          <p className="eyebrow mb-2">{t("vault.labels")}</p>
          <div className="flex flex-wrap gap-2">
            {v.vault_labels.map(l => (
              <span key={`${l.proof_id}-${l.label}`} className={`px-2 py-1 rounded-lg font-mono text-[10px] ${l.expired ? "tag-red" : l.eligible === "oui" ? "tag-green" : "tag-orange"}`} data-testid={`vault-label-${l.label}`}>
                {l.label} · {l.country}{l.expired ? " · expiré" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="hairline">
        {(v.results || []).map((r, i) => {
          const st = STATUS_STYLE[r.status] || STATUS_STYLE.non_couvert;
          return (
            <div key={r.claim_id} className={`p-4 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`vault-result-${r.claim_id}`}>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded-lg font-mono text-[10px] inline-flex items-center gap-1 ${st.cls}`}>
                  <st.Icon className="w-3 h-3" /> {t(st.key)}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">{r.category}</span>
                {r.label && <span className="font-mono text-[10px] text-primary">{r.label}</span>}
                {r.label_url && <a href={r.label_url} target="_blank" rel="noreferrer" className="text-primary"><ExternalLink className="w-3 h-3" /></a>}
              </div>
              <p className="font-serif text-sm leading-snug">« {r.excerpt} »</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{r.justification}</p>
            </div>
          );
        })}
      </div>

      {(v.gap_recommendations || []).length > 0 && (
        <div>
          <p className="eyebrow mb-2">{t("vault.gap.title")}</p>
          <div className="space-y-3">
            {v.gap_recommendations.map(g => (
              <div key={g.category} className="rounded-lg border border-border p-3" data-testid={`vault-gap-${g.category}`}>
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{g.category} · {g.claims_count}</p>
                <p className="text-sm mt-1">{g.action}</p>
                {(g.suggested_labels || []).map(s => (
                  <p key={s.label} className="text-xs text-muted-foreground mt-1">
                    → {s.label} ({s.country}{s.issuer ? ` · ${s.issuer}` : ""})
                    {s.official_url && <a href={s.official_url} target="_blank" rel="noreferrer" className="text-primary ml-1 link-underline">↗</a>}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {(history || []).length > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-2"><History className="w-3.5 h-3.5" /> {t("vault.history")}</p>
          <div className="space-y-1">
            {history.map(h => (
              <p key={h.version_label + h.verified_at} className="font-mono text-[11px] text-muted-foreground" data-testid={`vault-history-${h.version_label}`}>
                <b className="text-foreground">{h.version_label}</b> · {new Date(h.verified_at).toLocaleString()} · {h.score_before} → {h.score_after}/100 ({h.grade_after}) · {h.covered_count} {t("vault.status.covered").toLowerCase()}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone, testid }) {
  const color = tone === "green" ? "text-[hsl(var(--status-green))]" : tone === "orange" ? "text-[hsl(29_87%_50%)]" : "text-[hsl(var(--status-red))]";
  return (
    <div className="hairline p-4" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{label}</p>
      <p className={`text-2xl font-serif ${color}`}>{value}</p>
    </div>
  );
}
